<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useTrip } from '@/stores/trip';
import { useToast } from '@/composables/useToast';
import { STATIONS, STATION_INDEX } from '@/data/graph';
import { buildCostMatrix, solveTSP } from '@/core/tsp';
import { fmtTime } from '@/core/format';
import type { TspResult, TspShape } from '@/types';
import SegmentButton from '@/components/ui/SegmentButton.vue';

/**
 * Modale d'optimisation TSP : réorganise les étapes pour minimiser le temps total.
 *
 * L'utilisateur peut :
 *   - Verrouiller des étapes à leur position courante (1ère, dernière…)
 *   - Choisir entre une ligne (open) et une boucle (loop, retour au départ)
 *
 * Le calcul est synchrone et s'exécute à chaque changement (`watch` sur shape + locked).
 * Pour les jeux de données réalistes (3-10 étapes), c'est instantané.
 */
const props = defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const trip = useTrip();
const { show: showToast } = useToast();

const optState = reactive<{
  locked: Set<number>;
  shape: TspShape;
  result: TspResult | null;
  currentCost: number;
}>({
  locked: new Set(),
  shape: 'open',
  result: null,
  currentCost: 0,
});

// Quand on ouvre la modale, on reset l'état et on verrouille la 1ère étape par défaut
watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    if (trip.state.routeStops.length < 3) {
      showToast('Il faut au moins 3 étapes pour optimiser', 'error');
      emit('close');
      return;
    }
    optState.locked = new Set([0]);
    optState.shape = 'open';
    optState.result = null;
    recompute();
  },
);

// Recalcul à chaque changement de verrous / forme
function recompute() {
  if (!props.open) return;
  const stops = trip.state.routeStops;
  const n = stops.length;
  const lockedAt = new Array(n).fill(-1);
  optState.locked.forEach((i) => {
    lockedAt[i] = i; // étape figée à sa position d'origine
  });

  // Pour le TSP on veut MINIMISER LE TEMPS RÉEL, pas le temps perçu.
  // En mode 'night', findPath applique un bonus 0.35 sur les trains de nuit,
  // ce qui fausse la matrice de coûts et affiche un gain "fantôme" qui ne se
  // retrouvera pas dans le calendrier (qui utilise toujours la durée réelle).
  // On force donc 'train' pour 'night' afin que le TSP raisonne sur les vraies
  // durées (le mode utilisateur reste inchangé après application de l'ordre).
  const matrixMode =
    trip.state.routeMode === 'night' ? 'train' : trip.state.routeMode;

  const matrix = buildCostMatrix(stops, matrixMode);

  // Coût actuel (avant optimisation) — pour calculer le gain
  let currentCost = 0;
  for (let i = 0; i < n - 1; i++) {
    const c = matrix[i][i + 1];
    if (c !== Infinity) currentCost += c;
  }
  if (optState.shape === 'loop' && n > 1) {
    const c = matrix[n - 1][0];
    if (c !== Infinity) currentCost += c;
  }
  optState.currentCost = currentCost;

  optState.result = solveTSP(matrix, lockedAt, optState.shape);
}

function toggleLock(idx: number) {
  if (optState.locked.has(idx)) optState.locked.delete(idx);
  else optState.locked.add(idx);
  recompute();
}

function setShape(s: TspShape) {
  optState.shape = s;
  recompute();
}

function apply() {
  const result = optState.result;
  if (!result || !result.order.length || result.cost === Infinity) {
    showToast('Aucune amélioration possible', 'error');
    emit('close');
    return;
  }
  const newStops = result.order.map((idx) => trip.state.routeStops[idx]);
  trip.replaceStops(newStops);
  emit('close');
  showToast('Itinéraire réorganisé');
}

// Bind Esc pour fermer (lifecycle)
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.open) emit('close');
}
onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

// Helpers pour le rendu
const previewOrder = computed(() => {
  const result = optState.result;
  if (!result || !result.order.length) return [];
  return result.order.map((idx) => ({
    idx,
    name: STATIONS[STATION_INDEX[trip.state.routeStops[idx].id]].name,
    locked: optState.locked.has(idx),
  }));
});

const previewResult = computed(() => {
  const result = optState.result;
  if (!result || result.cost === Infinity) return null;
  const saved = optState.currentCost - result.cost;
  return {
    total: fmtTime(result.cost),
    saved,
    savedLabel:
      saved > 0
        ? `−${fmtTime(saved)} gagnées`
        : saved === 0
          ? "L'ordre actuel est déjà optimal"
          : "Pas d'amélioration possible",
    isGain: saved > 0,
  };
});

function onBackdropClick(e: MouseEvent) {
  if (e.target === e.currentTarget) emit('close');
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200"
      leave-active-class="transition duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        @click="onBackdropClick"
      >
        <div
          class="relative w-full max-w-lg bg-surface rounded-lg shadow-2xl max-h-[90vh] flex flex-col"
        >
          <!-- Header -->
          <header
            class="flex items-center justify-between px-5 py-4 border-b border-DEFAULT"
          >
            <h2 class="flex items-center gap-2 font-medium text-lg">
              <i class="ti ti-wand text-accent" aria-hidden="true"></i>
              Optimiser l'ordre
            </h2>
            <button
              type="button"
              class="text-text-faint hover:text-text"
              aria-label="Fermer"
              @click="emit('close')"
            >
              <i class="ti ti-x" aria-hidden="true"></i>
            </button>
          </header>

          <!-- Body -->
          <div class="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <p class="text-sm text-text-muted">
              Verrouille les étapes que tu veux garder à leur place (départ, arrivée…).
              L'algorithme trouve le meilleur ordre pour le reste.
            </p>

            <!-- Liste des étapes -->
            <ol class="space-y-2">
              <li
                v-for="(stop, i) in trip.state.routeStops"
                :key="i"
                :class="[
                  'flex items-center gap-3 px-3 py-2 rounded-md',
                  optState.locked.has(i) ? 'bg-accent/10' : 'bg-surface-muted/40',
                ]"
              >
                <span
                  class="w-6 h-6 flex-shrink-0 rounded-full bg-accent/20 text-accent text-xs font-semibold flex items-center justify-center"
                >
                  {{ i + 1 }}
                </span>
                <span class="flex-1 font-medium">{{
                  STATIONS[STATION_INDEX[stop.id]].name
                }}</span>
                <button
                  type="button"
                  :class="[
                    'inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors duration-160',
                    optState.locked.has(i)
                      ? 'bg-accent text-white'
                      : 'bg-surface border border-DEFAULT text-text-muted hover:text-text',
                  ]"
                  @click="toggleLock(i)"
                >
                  <i
                    :class="['ti', optState.locked.has(i) ? 'ti-lock' : 'ti-lock-open']"
                    aria-hidden="true"
                  ></i>
                  {{ optState.locked.has(i) ? 'Fixe' : 'Libre' }}
                </button>
              </li>
            </ol>

            <!-- Forme du parcours -->
            <div>
              <div class="text-sm font-medium mb-2">Forme du parcours</div>
              <div class="flex gap-1 p-1 bg-surface-muted rounded-md">
                <SegmentButton
                  :active="optState.shape === 'open'"
                  icon="ti-line"
                  @click="setShape('open')"
                >
                  Ligne A→B
                </SegmentButton>
                <SegmentButton
                  :active="optState.shape === 'loop'"
                  icon="ti-rotate"
                  @click="setShape('loop')"
                >
                  Boucle (retour)
                </SegmentButton>
              </div>
            </div>

            <!-- Preview -->
            <div class="p-4 bg-surface-muted/60 rounded-md">
              <div class="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                Ordre proposé
              </div>
              <div
                v-if="previewOrder.length === 0"
                class="text-sm text-text-muted"
              >
                Impossible de trouver un ordre valide. Essaie de retirer des verrous.
              </div>
              <div v-else class="space-y-3">
                <div class="flex flex-wrap items-center gap-2 text-sm">
                  <template v-for="(item, pos) in previewOrder" :key="pos">
                    <span
                      :class="[
                        'inline-flex items-center gap-1 px-2 py-1 rounded-md',
                        item.locked
                          ? 'bg-accent/20 text-accent'
                          : 'bg-surface border border-DEFAULT',
                      ]"
                    >
                      {{ pos + 1 }}. {{ item.name }}
                      <i v-if="item.locked" class="ti ti-lock text-xs" aria-hidden="true"></i>
                    </span>
                    <span
                      v-if="pos < previewOrder.length - 1"
                      class="text-text-faint"
                      >→</span
                    >
                  </template>
                  <template v-if="optState.shape === 'loop' && previewOrder.length > 0">
                    <span class="text-text-faint">↩</span>
                    <span
                      class="inline-flex items-center px-2 py-1 rounded-md bg-surface border border-DEFAULT opacity-70"
                    >
                      {{ previewOrder[0].name }}
                    </span>
                  </template>
                </div>

                <div
                  v-if="previewResult"
                  class="flex items-center justify-between text-sm pt-3 border-t border-DEFAULT"
                >
                  <span>
                    Nouveau total : <strong>{{ previewResult.total }}</strong>
                  </span>
                  <span
                    :class="[
                      'font-medium',
                      previewResult.isGain ? 'text-green-700' : 'text-text-muted',
                    ]"
                  >
                    {{ previewResult.savedLabel }}
                  </span>
                </div>

                <!-- Note quand le mode global est 'night' : on optimise sur la
                     durée réelle, pas perçue, sinon le gain affiché serait
                     virtuel et ne correspondrait pas au calendrier final. -->
                <p
                  v-if="trip.state.routeMode === 'night'"
                  class="text-xs text-text-muted pt-2"
                >
                  <i class="ti ti-info-circle" aria-hidden="true"></i>
                  Optimisation calculée sur la durée réelle de trajet. Le mode
                  « Nuit » est conservé après application — les trains de nuit
                  resteront privilégiés dans le calendrier final.
                </p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <footer class="flex justify-end gap-2 px-5 py-4 border-t border-DEFAULT">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-text-muted hover:text-text rounded-md transition-colors duration-160"
              @click="emit('close')"
            >
              Annuler
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-white rounded-md hover:bg-accent-hover transition-colors duration-160"
              @click="apply"
            >
              <i class="ti ti-check" aria-hidden="true"></i>
              Appliquer
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
